import React, { useState, useMemo } from "react";
import { FaPencilAlt, FaTrashAlt } from "react-icons/fa";
import "./AdminTools.css";
// La veste dei due comandi di riga (`.qpsc-pay-icon-btn`, `.qpsc-pay-tools`)
// vive nel foglio della finestra Payments: si importa da qui come fa
// ClinicPaymentModal.jsx, invece di riscriverla (calco, CALENDAR ADMIN E2).
import "./PaymentsModal.css";
import "./EventsTable.css";
import { dataEvento } from "../utils/calendario";

// Data e giorno si leggono con `dataEvento` (G5-a): i due formati che i dati
// portano, `YYYY-MM-DD` e `DD-MM-YYYY`, come data LOCALE.
function formatDate(dateString) {
  const dateObj = dataEvento({ date: dateString });
  if (!dateObj) return "";
  return dateObj.toLocaleDateString("en-AU", { day: "2-digit", month: "2-digit", year: "numeric" });
}
function formatDay(dateString) {
  const dateObj = dataEvento({ date: dateString });
  if (!dateObj) return "";
  return dateObj.toLocaleDateString("en-AU", { weekday: "short" }).toUpperCase();
}

// CALENDAR ADMIN E2 (27/9/2026): Actions è l'ULTIMA colonna (era penultima,
// prima di Notes); Day non si ordina più (`noSort`: non ha un ramo in
// `compareEvents`, e la freccia compariva senza effetto). Le classi portano
// il prefisso proprio `ccl-et-` — `col-actions` era un nome nudo condiviso
// con Club Finance, che da lì le passava `nowrap` e `max-width`.
const columns = [
  { key: "date", label: "Date", className: "ccl-et-col-date" },
  { key: "day", label: "Day", className: "ccl-et-col-day", noSort: true },
  { key: "time", label: "Time", className: "ccl-et-col-time" },
  { key: "homeAway", label: "H/A", className: "ccl-et-col-ha" },
  { key: "type", label: "Type", className: "ccl-et-col-type" },
  { key: "team", label: "Team", className: "ccl-et-col-team" },
  { key: "opponent", label: "Opponent", className: "ccl-et-col-opponent" },
  { key: "location", label: "Location", className: "ccl-et-col-location" },
  { key: "pitch", label: "Pitch", className: "ccl-et-col-pitch" },
  { key: "notes", label: "Notes", className: "ccl-et-col-notes" },
  { key: "actions", label: "Actions", className: "ccl-et-col-actions", noSort: true }
];

// Ogni campo ha il ripiego `|| ""` (E2, T9): un evento privo del campo non
// deve far cadere l'ordinamento con un TypeError.
function compareEvents(a, b, sortBy, direction) {
  let res = 0;
  if (sortBy === "date") {
    res = (a.date || "").localeCompare(b.date || "");
    if (res === 0) res = (a.time || "").localeCompare(b.time || "");
  } else if (sortBy === "time") {
    res = (a.time || "").localeCompare(b.time || "");
  } else if (sortBy === "team") {
    res = (a.team || "").localeCompare(b.team || "");
  } else if (sortBy === "homeAway") {
    res = (a.homeAway || "").localeCompare(b.homeAway || "");
  } else if (sortBy === "type") {
    res = (a.type || "").localeCompare(b.type || "");
  } else if (sortBy === "pitch") {
    res = (a.pitch || "").localeCompare(b.pitch || "");
  } else if (sortBy === "opponent") {
    res = (a.opponent || "").localeCompare(b.opponent || "");
  } else if (sortBy === "location") {
    res = (a.location || "").localeCompare(b.location || "");
  } else if (sortBy === "notes") {
    res = (a.notes || "").localeCompare(b.notes || "");
  }
  return direction === "asc" ? res : -res;
}

// Veste della pastiglia Type (deroga §1.5 B, calco di MyCalendar): TRE rami,
// non il ternario allenamento-sì/no di MyCalendar — lì «Event» prende la
// veste rossa della partita, qui ha la sua (testo secondario).
function typeModifier(type) {
  if (type === "Game") return "game";
  if (type === "Training") return "training";
  return "event";
}

// ⚠️ QUESTO COMPONENTE NON CONOSCE I RUOLI, ED È VOLUTO (P1, 8/8/2026).
//
// Prima riceveva `userRole` grezzo dal padre e decideva da sé, con
// `userRole !== "Player"`: un gate scritto al contrario, che apriva i
// comandi a chiunque non fosse esattamente un "Player" — quindi anche a un
// genitore, e anche a un giocatore scritto "PLAYER" in maiuscolo.
//
// Ora riceve `canEditEvents`, un permesso GIÀ DECISO da CalendarClub con la
// sede unica (`hasRole`). Il vantaggio non è di forma: il permesso resta
// calcolato UNA VOLTA SOLA, nella stessa riga che governa «+ Add Event» ed
// «Export CSV» nella barra, e la tabella non può più divergere dalla barra.
// EventsTable torna a essere ciò che è: sola presentazione.
const EventsTable = ({
  events,
  onEditEvent,
  onDeleteEvent,
  canEditEvents,
}) => {
  const [sortBy, setSortBy] = useState("date");
  const [sortDirection, setSortDirection] = useState("asc");

  // RESA — UNA SOLA TABELLA (CALENDAR ADMIN E2, 27/9/2026, calco di User
  // List). Non c'è più un ramo JSX per il telefono né lo stato `isMobile`
  // con la soglia a 650px: sotto i 768px la stessa `<table>` si ridispone a
  // card dal foglio (`td::before { content: attr(data-label) }`), sopra è
  // tabella. Per questo ogni `<td>` porta il `data-label` della sua colonna,
  // e così Time e H/A compaiono anche su telefono (le vecchie card li
  // perdevano).

  const sortedEvents = useMemo(() => {
    if (!events || !events.length) return [];
    return [...events].sort((a, b) => compareEvents(a, b, sortBy, sortDirection));
  }, [events, sortBy, sortDirection]);

  function handleSort(col) {
    if (col.noSort) return;
    if (sortBy === col.key) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(col.key);
      setSortDirection("asc");
    }
  }

  function getSortIcon(col) {
    if (col.noSort) return null;
    if (sortBy === col.key) {
      return <span className="ccl-et-sort">{sortDirection === "asc" ? "▲" : "▼"}</span>;
    }
    return null;
  }

  return (
  <div className="ccl-et-wrap">
    <table className="ccl-et-table">
      <thead>
        <tr>
          {columns.map((col) => (
            <th
              key={col.key}
              className={col.noSort ? col.className : `${col.className} ccl-et-th--sortable`}
              onClick={() => handleSort(col)}
            >
              {col.label}
              {getSortIcon(col)}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sortedEvents.length === 0 ? (
          <tr>
            <td colSpan={columns.length} data-label="" className="ccl-et-msg">No events for this week.</td>
          </tr>
        ) : (
          sortedEvents.map((event) => (
            <tr
              key={event.id}
              className={event.type === "Game" ? "ccl-et-row--game" : undefined}
            >
              <td className="ccl-et-col-date" data-label="Date">{formatDate(event.date)}</td>
              <td className="ccl-et-col-day" data-label="Day">{formatDay(event.date)}</td>
              <td className="ccl-et-col-time" data-label="Time">{event.time}</td>
              <td className="ccl-et-col-ha" data-label="H/A">{event.homeAway}</td>
              <td className="ccl-et-col-type" data-label="Type">
                {event.type && (
                  <span className={`qpsc-pill ccl-et-type--${typeModifier(event.type)}`}>{event.type}</span>
                )}
              </td>
              <td className="ccl-et-col-team" data-label="Team">{event.team}</td>
              <td className="ccl-et-col-opponent" data-label="Opponent">{event.type === "Game" ? event.opponent : ""}</td>
              <td className="ccl-et-col-location" data-label="Location">{event.location}</td>
              <td className="ccl-et-col-pitch" data-label="Pitch">{event.pitch}</td>
              <td className="ccl-et-col-notes" data-label="Notes">{event.notes}</td>
              <td className="ccl-et-col-actions" data-label="Actions">
                {canEditEvents && (
                  <span className="qpsc-pay-tools">
                    <button
                      type="button"
                      className="qpsc-ghost-btn qpsc-pay-icon-btn"
                      aria-label="Edit this event"
                      onClick={() => onEditEvent && onEditEvent(event)}
                    >
                      <FaPencilAlt aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      className="qpsc-ghost-btn qpsc-pay-icon-btn"
                      aria-label="Delete this event"
                      onClick={() => {
                        if (window.confirm("Are you sure you want to delete this event?")) {
                          onDeleteEvent && onDeleteEvent(event.id);
                        }
                      }}
                    >
                      <FaTrashAlt aria-hidden="true" />
                    </button>
                  </span>
                )}
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);
};
export default EventsTable;